import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { Http, Request, RequestOptionsArgs, Response, Headers } from '@angular/http';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { StringUtils } from '../utils';
import { NotificationService } from './notification.service';
import { of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class HttpService {
	constructor(
		private _http: Http,
		private _authService: AuthService,
		private _router: Router
	) { }

	request(url: string | Request, options?: RequestOptionsArgs): Observable<Response> {
		const token = this._authService.getToken();

		let headers;
		if (typeof url == 'string') {
			options = options || {};

			if (!options.headers) {
				options.headers = new Headers();
			}

			headers = options.headers;
		}
		else {
			headers = url.headers;
		}


		if (token) {
			headers.set('Authorization', `Bearer ${token}`);
		}
		headers.set('Content-Type', 'application/json; charset=utf-8');
		headers.set('X-Requested-With', 'XMLHttpRequest');

		return this._http.request(url, options).pipe(catchError(this.catchAuthError(this)));
	}

	get(url: string, options?: RequestOptionsArgs): Observable<Response> {
		return this._http.get(url, this.exOptions(url, options)).pipe(catchError(this.catchAuthError(this)));
	}

	post(url: string, body: any, options?: RequestOptionsArgs): Observable<Response> {
		return this._http.post(url, body, this.exOptions(url, options)).pipe(catchError(this.catchAuthError(this)));
	}

	put(url: string, body: any, options?: RequestOptionsArgs): Observable<Response> {
		return this._http.put(url, body, this.exOptions(url, options)).pipe(catchError(this.catchAuthError(this)));
	}

	delete(url: string, options?: RequestOptionsArgs): Observable<Response> {
		return this._http.delete(url, this.exOptions(url, options)).pipe(catchError(this.catchAuthError(this)));
	}

	patch(url: string, body: any, options?: RequestOptionsArgs): Observable<Response> {
		return this._http.patch(url, body, this.exOptions(url, options)).pipe(catchError(this.catchAuthError(this)));
	}

	head(url: string, options?: RequestOptionsArgs): Observable<Response> {
		return this._http.head(url, this.exOptions(url, options)).pipe(catchError(this.catchAuthError(this)));
	}

	options(url: string, options?: RequestOptionsArgs): Observable<Response> {
		return this._http.options(url, this.exOptions(url, options)).pipe(catchError(this.catchAuthError(this)));
	}

	private exOptions(url: string, options?: RequestOptionsArgs): RequestOptionsArgs {
		if (!options) {
			options = {};
		}

		if (!options.headers) {
			options.headers = new Headers();
		}

		let headers = options.headers;

		const token = this._authService.getToken();
		if (token) {
			headers.set('Authorization', `Bearer ${token}`);
		}
		headers.set('Content-Type', 'application/json; charset=utf-8');
		headers.set('X-Requested-With', 'XMLHttpRequest');

		return options;
	}

	private catchAuthError(self: HttpService) {
		// we have to pass HttpService's own instance here as `self`
		return (res: Response | any): Observable<any> => {
			if (res.status == 401 || res.status == 403) {
				// if not authenticated
				this._authService
					.logout()
					.subscribe(() => this._router.navigateByUrl('/account/login'));
				return throwError(res);
			}

			if (res instanceof Response) {
				let response = res as Response;

				if (res.status == 0) {
					console.warn('Server is disconnected!');
					this._router.navigateByUrl('/disconnected');
					return of(res);
				}
				else if (res.status == 400) {
					let errors: Object;
					try {
						errors = res.json();
					}
					catch (e) {
						errors = { '_': res.text() };
					}
					finally { }

					return throwError(new BadRequestError(errors));
				}
				else if (res.status == 500) {
					let errors: Object;
					try {
						errors = res.json();
					}
					catch (e) {
						errors = { message: res.text() };
					}
					finally { }

					return throwError(new RequestError(errors));
				}

				let err = new RequestError(response);

				err.message = response.text();

				return throwError(new RequestError(response));
			} else {
				const msg = res.message ? res.message : res.toString();
				let err: RequestError;

				if (res.status == 400) {
					err = new BadRequestError({ '_': msg });
				}
				else {
					err = new RequestError(res);
					err.message = msg;
				}

				return throwError(err);
			}
		};
	}
}

@Injectable()
export class ErrorHandlerService {
	constructor(
		private _notifications: NotificationService
	) {
	}

	handle(res: BadRequestError | Response | any, form?: FormGroup) {
		ErrorHandler.handle(res, msg => this._notifications.error(msg), form);
	}
}

export class ErrorHandler {
	public static handle(res: BadRequestError | Response | any, action?: HandleDelegate, form?: FormGroup) {
		let msg = '';
		if (res instanceof BadRequestError) {
			msg = (res as BadRequestError).message;

			Object
				.keys(res.validations)
				.forEach((key: string) => {
					let c = form.get(key);
					if (c != null) {
						c.setErrors({
							remote: res.validations[key]
						});
					}
				});
		}
		else if (res instanceof RequestError) {
			msg = (res as BadRequestError).message;
		}
		else if (res && res.response) {
			msg = res.response.text();
		}
		else {
			msg = res.message;
		}

		if (action != null) {
			action(msg);
		}
		else {
			console.error(msg);
		}
	}
}

export interface HandleDelegate {
	(message: string): void;
}

export interface OperationResult {
	success: boolean;
	message: string;
}

export class RequestError {
	message: string = '';
	response: Response | any;

	constructor(res: Response | any) {
		this.response = res;
		if (res) {
			this.message = res.message || '';
		}
	}
}

export class BadRequestError extends RequestError {
	validations: any = {};

	constructor(body: Object, res?: Response | any) {
		super(res);

		if (body) {
			this.validations = {};

			Object.keys(body).forEach((key: string) => {
				if (body.hasOwnProperty(key)) {
					let errors = body[key];
					if (Array.isArray(errors)) {
						errors = errors.join('\r\n');
					}
					this.message += errors + '\r\n';
					key = StringUtils.firstToLowerCase(key);
					this.validations[key] = errors;
				}
			});
		}

		this.response = res;
	}
}


// WEBPACK FOOTER //
// ./node_modules/angular-router-loader/src!./node_modules/angular2-template-loader!./app/core/services/http.service.ts